import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ErrorBoundaryWrapper } from '@/components/modules/error/ErrorBoundaryWrapper';
import { useAuth0 } from '@auth0/auth0-react';

import { ROUTES, ROUTE_CONFIG, Locale } from '@/constants/route-config';

import {
  extractLocaleFromPath,
  getLocalizedPath,
  cleanupLocalePath,
  simplifyLanguageCode,
} from '@/utils/locale';

import {
  LocaleValidator,
  LocaleBaseRedirect,
  BasePathRedirect,
  RootRedirect,
} from './LocaleRoutes';

import { CircularProgress, Box } from '@mui/material';
import { RouteWithHelmet } from '@/RouteWithHelmet';

/**
 * Loading component displayed while routes are being lazy-loaded
 */
const RouteLoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      height: '50vh',
    }}
  >
    <CircularProgress />
  </Box>
);

/**
 * Main application routes component
 *
 * The route structure is as follows:
 * 3. Check path and handle redirects, for example:
 *  - / -> /en/dashboard
 *  - /dashboard -> /en/dashboard
 *  - /en -> /en/dashboard
 * 4. Exact routes with locale
 * 5. 404
 */
const AppRoutes: React.FC = () => {
  const location = useLocation();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth0();

  useEffect(() => {
    // First, make sure the path is cleaned up and without duplicate locales
    const cleanPath = cleanupLocalePath(location.pathname);
    if (cleanPath !== location.pathname) {
      // Redirect to the clean path silently
      navigate(cleanPath, { replace: true });
      return;
    }

    // When route changes, extract locale from URL path and synchronize with i18n
    const { locale } = extractLocaleFromPath(cleanPath);
    if (locale && locale !== simplifyLanguageCode(i18n.language)) {
      // Only change language if it's different from current language (after simplification)
      i18n.changeLanguage(locale);
    }
  }, [location.pathname, i18n, navigate]);

  const simplifiedLanguages = i18n.languages
    .map((lang: string) => simplifyLanguageCode(lang))
    .filter(
      (lang: string, index: number, self: string[]) =>
        self.indexOf(lang) === index,
    ); // Remove duplicates

  const baseRoutes = Object.values(ROUTES).filter(
    (route) =>
      // Only include static routes that should have redirects
      // Exclude wildcard, parameterized routes, and the root path (which has special handling)
      route !== ROUTES.ROOT &&
      route !== ROUTES.NOT_FOUND &&
      !route.includes(':'),
  );

  return (
    <ErrorBoundaryWrapper>
      <RouteWithHelmet>
        <Suspense fallback={<RouteLoadingFallback />}>
          <Routes>
            <Route path={ROUTES.ROOT} element={<RootRedirect />} />

            {baseRoutes.map((path) => {
              // Ensure we don't create duplicate routes
              const normalizedPath = path.startsWith('/') ? path : `/${path}`;
              return (
                <Route
                  key={`redirect-${normalizedPath}`}
                  path={normalizedPath}
                  element={<BasePathRedirect />}
                />
              );
            })}

            {/* Handle locale-only paths */}
            {simplifiedLanguages.map((lang) => (
              <Route
                key={`locale-${lang}`}
                path={`/${lang}`}
                element={<LocaleBaseRedirect />}
              />
            ))}

            <Route element={<LocaleValidator />}>
              {simplifiedLanguages.map((lang: Locale) =>
                ROUTE_CONFIG.map((route) => {
                  if (route.path === ROUTES.NOT_FOUND) return null;

                  const localizedPath = getLocalizedPath(route.path, lang);
                  // Create element with proper suspense handling
                  const RouteElement = route.elementComponent ? (
                    <ErrorBoundaryWrapper>
                      <Suspense fallback={<RouteLoadingFallback />}>
                        <route.elementComponent />
                      </Suspense>
                    </ErrorBoundaryWrapper>
                  ) : (
                    route.element
                  );

                  const ProtectedRouteElement =
                    (route.requireAuth ?? true) ? (
                      <RequireAuth
                        isAuthenticated={isAuthenticated}
                        isLoading={isLoading}
                      >
                        {RouteElement}
                      </RequireAuth>
                    ) : (
                      RouteElement
                    );

                  return (
                    <Route
                      key={`${lang}-${route.path}`}
                      path={localizedPath}
                      element={ProtectedRouteElement}
                    />
                  );
                }),
              )}
            </Route>

            {/* Catch-all 404 route */}
            {/* For any unmatched path, try to localize it */}
            <Route path="*" element={<BasePathRedirect />} />
          </Routes>
        </Suspense>
      </RouteWithHelmet>
    </ErrorBoundaryWrapper>
  );
};

type RequireAuthProps = {
  children: React.ReactNode;
  isAuthenticated: boolean;
  isLoading: boolean;
};

const RequireAuth: React.FC<RequireAuthProps> = ({
  children,
  isAuthenticated,
  isLoading,
}) => {
  // Show loading only while Auth0 SDK is initializing
  if (isLoading) return <RouteLoadingFallback />;

  // Public pages remain available; protected pages must never mount as a guest.
  if (!isAuthenticated) {
    return <Navigate to={getLocalizedPath(ROUTES.DISCOVER)} replace />;
  }

  return <>{children}</>;
};

export default AppRoutes;
